import { contentTypeLabel } from "@/lib/tina/content-types";
import { asRecord, firstText } from "@/lib/tina/value-readers";
import type { PipelineItem } from "@/types/tina";

export { asRecord, firstText };

/** View-model for the discover-seed new/known breakdown panel. */
export interface DiscoverBreakdown {
  discoveredCount: number;
  newCount: number;
  knownCount: number | null;
  queuedCount: number;
  checkedKnown: boolean;
  newUrls: string[];
  knownUrls: string[];
  /** new + known — the full discovered set, for the "All discovered" list. */
  allUrls: string[];
  /** Fewer new URLs were queued than found, i.e. the per-run discover cap trimmed some. */
  capped: boolean;
  /** Stored URL lists were truncated for size; counts above stay exact. */
  truncated: boolean;
}

export function itemDiscoverBreakdown(
  item: PipelineItem,
): DiscoverBreakdown | null {
  const summary = item.discover_summary;
  if (!summary) return null;
  return {
    discoveredCount: summary.discovered_count,
    newCount: summary.new_count,
    knownCount: summary.known_count,
    queuedCount: summary.queued_count,
    checkedKnown: summary.checked_known,
    newUrls: summary.new_urls,
    knownUrls: summary.known_urls,
    allUrls: [...summary.new_urls, ...summary.known_urls],
    capped: summary.queued_count < summary.new_count,
    truncated: summary.truncated,
  };
}

export function discoverBreakdownLabel(breakdown: DiscoverBreakdown): string {
  const known = breakdown.checkedKnown
    ? (breakdown.knownCount ?? 0).toLocaleString()
    : "—";
  return `${breakdown.discoveredCount.toLocaleString()} discovered · ${breakdown.newCount.toLocaleString()} new · ${known} known`;
}

export function itemSourceUrl(item: PipelineItem): string | undefined {
  const metadata = asRecord(item.metadata);
  const candidates = [
    item.url,
    metadata.source_url,
    metadata.source_page_url,
    metadata.sourceUrl,
    metadata.url,
    metadata.id,
  ];
  // A web link first: a sports follower's `url` is a `highlightly://` identity that opens nothing,
  // and Tina puts a clickable `metadata.source_url` beside it. Other items keep their own url.
  const web = candidates.find(
    (value): value is string =>
      typeof value === "string" && /^https?:\/\//i.test(value),
  );
  return web ?? firstText(...candidates);
}

export function itemLineageLabel(item: PipelineItem): string | undefined {
  if (isDiscoverSeedItem(item)) return "discover seed";
  if (!item.is_primary) {
    const instance = itemInstanceNumber(item);
    return instance ? `follower #${instance}` : "follower";
  }
  if (item.group_id) return "primary";
  if (item.parent_event_id) return "source item";
  return undefined;
}

export function itemDiscoveredCount(item: PipelineItem): number {
  const value = Number(item.discovered_count ?? 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function discoveredUrlLabel(count: number): string {
  return `${count.toLocaleString()} discovered URL${count === 1 ? "" : "s"}`;
}

export function itemSourceTitle(item: PipelineItem): string {
  const metadata = asRecord(item.metadata);
  return (
    firstText(
      metadata.extracted_name,
      metadata.movie_title,
      metadata.extracted_movie_title,
      metadata.resolved_movie_title,
      metadata.venue_name,
      metadata.resolved_venue_name,
    ) ?? contentTypeLabel(item.content_type)
  );
}

export function itemInstanceNumber(item: PipelineItem): number | null {
  const metadata = asRecord(item.metadata);
  const raw =
    metadata.extracted_event_index ?? metadata.extracted_screening_index;
  const value = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(value) || value < 0) return null;
  return value + 1;
}

export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "-";
}

export function urlTitle(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname}`;
  } catch {
    return url;
  }
}

// Re-exported so this module's existing importers keep their import path while
// there is exactly one implementation. Behaviour change
// to accept: a mapped type now renders "Music", not "Music Event".
export { contentTypeLabel };

export function isDiscoverSeedItem(item: PipelineItem): boolean {
  return (
    item.parent_event_id === null &&
    item.current_stage === "discover" &&
    !item.url
  );
}
